import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import { NotificationContext } from './context';
import NotificationHub from './NotificationHub';
import NotificationBlock from './NotificationBlock';
import Notification from './Notification';
import { EMPTY_PROPS } from '../helpers/types';
const actions = {
  ENQUEUE: 'ENQUEUE',
  CLOSE: 'CLOSE',
  SET_ACTIVE: 'SET_ACTIVE',
  SET_INACTIVE: 'SET_INACTIVE'
};
const initialState = {
  notifications: [],
  active: true,
  variant: null,
  type: 'append'
};
let counter = 0;
const getCounter = () => counter++;

// Determine whether notification can be auto-closed
const isAutoClosable = n => n.delay === undefined || n.delay > 0;
const notifyOnClose = (newNotifications, oldNotifications) => {
  if (newNotifications.length < oldNotifications.length) {
    const newNotificationsKeys = newNotifications.map(n => n.key);
    const removedNotifications = oldNotifications.filter(n => !newNotificationsKeys.includes(n.key));
    removedNotifications.forEach(n => n.onClose?.(n.key));
  }
};
const reducer = (state, action) => {
  switch (action.type) {
    case actions.SET_ACTIVE:
      {
        const newNotifications = state.notifications.filter(n => !isAutoClosable(n) || !n.closed);

        // notify about removed notifications
        notifyOnClose(newNotifications, state.notifications);
        return {
          ...state,
          active: true,
          notifications: newNotifications
        };
      }
    case actions.SET_INACTIVE:
      {
        return {
          ...state,
          active: false
        };
      }
    case actions.ENQUEUE:
      {
        const key = action.key || action.notification.key || `${getCounter()}-${Date.now()}-${Math.random()}`;
        return {
          ...state,
          notifications: state.type === 'append' ? [...state.notifications, {
            key,
            ...action.notification
          }] : [{
            key,
            ...action.notification
          }, ...state.notifications]
        };
      }
    case actions.CLOSE:
      {
        let newNotifications = [];
        if (action.force) {
          // force closing current notification
          newNotifications = state.notifications.filter(n => n.key !== action.key);
        } else {
          // otherwise, wait until all notifications are closed
          newNotifications = state.notifications.map(n => n.key === action.key ? {
            ...n,
            closed: true
          } : n);
        }

        // determine whether all closable notifications already auto-closed
        if (state.active && newNotifications.filter(n => isAutoClosable(n) && !n.closed).length <= 0) {
          newNotifications = newNotifications.filter(n => !isAutoClosable(n) || !n.closed);
        }

        // notify about removed notifications
        notifyOnClose(newNotifications, state.notifications);

        // just return current state
        return {
          ...state,
          notifications: newNotifications
        };
      }
    default:
      throw new Error('Wrong action passed to NotificationProvider');
  }
};
export const useNotification = (context = NotificationContext) => {
  const {
    enqueueNotification,
    closeNotification
  } = useContext(context);
  const show = useCallback((notification, variant = 'info') => enqueueNotification(notification, variant), [enqueueNotification]);
  const close = useCallback((key, variant = 'info', force = true) => closeNotification(key, variant, force), [closeNotification]);
  const showInfo = useCallback(notification => show(notification, 'info'), [show]);
  const showBlock = useCallback(notification => show(notification, 'block'), [show]);
  const showStatus = useCallback(notification => show(notification, 'status'), [show]);
  const closeInfo = useCallback((key, force = true) => close(key, 'info', force), [close]);
  const closeBlock = useCallback((key, force = true) => close(key, 'block', force), [close]);
  const closeStatus = useCallback((key, force = true) => close(key, 'status', force), [close]);
  return useMemo(() => ({
    show,
    close,
    showInfo,
    showBlock,
    showStatus,
    closeInfo,
    closeBlock,
    closeStatus,
    /* legacy */
    enqueueNotification,
    closeNotification
  }), [show, close, enqueueNotification, closeNotification]);
};
const useNotificationReducer = (variant = 'info', type = 'append') => {
  const [{
    notifications
  }, dispatch] = useReducer(reducer, {
    ...initialState,
    variant,
    type
  });
  return {
    notifications,
    dispatch
  };
};
/**
 * NotificationProvider component.
 *
 * Keeps notifications in a state, provides methods to update them, and renders a NotificationHub
 * to display them.
 *
 * This is not needed to use the 'NotificationHub'. Another custom implementation can be used.
 */
const NotificationProvider = ({
  context: Context = NotificationContext,
  notificationHubProps = EMPTY_PROPS,
  notificationHubInfoProps = EMPTY_PROPS,
  notificationHubStatusProps = EMPTY_PROPS,
  notificationHubBlocksProps = EMPTY_PROPS,
  fixed = true,
  absolute = false,
  zIndex,
  width,
  top = 0,
  bottom = 0,
  right = 0,
  children
}) => {
  const refs = useRef({});
  const reducers = {
    info: useNotificationReducer('info', 'prepend'),
    status: useNotificationReducer('status', 'append'),
    block: useNotificationReducer('block', 'prepend')
  };
  const getReducer = (variant = 'info') => reducers[variant] || reducers.info;

  // display a new notification with the provided Notification props
  const enqueueNotification = useCallback((notification, variant = 'info') => {
    const key = notification.key || `${getCounter()}-${variant}-${Date.now()}-${Math.random()}`;
    const {
      dispatch
    } = getReducer(variant);
    dispatch({
      type: actions.ENQUEUE,
      notification,
      key
    });
    return {
      key,
      close: () => dispatch({
        type: actions.CLOSE,
        key,
        force: true
      })
    };
  }, []);

  // close the notification with the provided key
  const closeNotification = useCallback((key, variant = 'info', force = false) => {
    const {
      dispatch
    } = getReducer(variant);
    dispatch({
      type: actions.CLOSE,
      key,
      force
    });
  }, []);
  const contextValue = useMemo(() => ({
    enqueueNotification,
    closeNotification
  }), [enqueueNotification, closeNotification]);
  const setActive = useCallback((variant = 'info') => {
    const {
      dispatch
    } = getReducer(variant);
    dispatch({
      type: actions.SET_ACTIVE
    });
  }, []);
  const setInactive = useCallback((variant = 'info') => {
    const {
      dispatch
    } = getReducer(variant);
    dispatch({
      type: actions.SET_INACTIVE
    });
  }, []);
  const onBlockHover = useCallback(() => setInactive('block'), [setInactive]);
  const onBlockLeave = useCallback(() => setActive('block'), [setActive]);
  const onInfoHover = useCallback(() => setInactive('info'), [setInactive]);
  const onInfoLeave = useCallback(() => setActive('info'), [setActive]);
  const onStatusHover = useCallback(() => setInactive('status'), [setInactive]);
  const onStatusLeave = useCallback(() => setActive('status'), [setActive]);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: contextValue
  }, children, /*#__PURE__*/React.createElement(NotificationHub, _extends({
    position: 'top-right',
    variant: 'block',
    refs: refs,
    notifications: reducers.block.notifications,
    onCloseNotification: closeNotification,
    onHover: onBlockHover,
    onLeave: onBlockLeave,
    component: NotificationBlock,
    fixed: fixed,
    absolute: absolute,
    zIndex: zIndex,
    top: top,
    right: right
  }, notificationHubBlocksProps)), /*#__PURE__*/React.createElement(NotificationHub, _extends({
    position: 'top-center',
    variant: 'info',
    refs: refs,
    notifications: reducers.info.notifications,
    onCloseNotification: closeNotification,
    onHover: onInfoHover,
    onLeave: onInfoLeave,
    component: Notification,
    fixed: fixed,
    absolute: absolute,
    zIndex: zIndex,
    width: width,
    top: top
  }, notificationHubProps, notificationHubInfoProps)), /*#__PURE__*/React.createElement(NotificationHub, _extends({
    position: 'bottom-center',
    variant: 'status',
    refs: refs,
    notifications: reducers.status.notifications,
    onCloseNotification: closeNotification,
    onHover: onStatusHover,
    onLeave: onStatusLeave,
    component: Notification,
    fixed: fixed,
    absolute: absolute,
    zIndex: zIndex,
    width: width,
    bottom: bottom
  }, notificationHubStatusProps)));
};
export default NotificationProvider;